import React, { useContext, useEffect, useState } from 'react';
import { BrowserClient } from '@sentry/browser';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorPage } from '../components/ErrorPage/ErrorPage';
import * as Sentry from '@sentry/browser';
import { IAppContext } from '../client';
import { useViewport } from '../hooks/useViewport';

interface IErrorContext {
  setError(error: Error): void;
  error: Error | undefined;
  sentryClient: typeof Sentry;
}

export const ErrorContext = React.createContext<IErrorContext | undefined>(
  undefined,
);

export const ErrorsProvider: React.FC<{
  sentryClient?: BrowserClient;
  appContext: IAppContext;
}> = ({ children, appContext }) => {
  const {
    currentBreakpoint: { isDesktop, isMobile, isTablet },
  } = useViewport();

  useEffect(() => {
    Sentry.init({
      dsn: 'https://bf29edde8dd04899bb8c064ebf2c41e2@sentry.wixpress.com/2357',
      attachStacktrace: true,
      enabled: appContext?.isProduction,
      environment: process.env.NODE_ENV,
      release: process.env.npm_package_version,
    });

    Sentry.configureScope((scope) => {
      scope.setTag('locale', appContext.locale);
      scope.setTag('origin', window.location.href);
      scope.setTag('referralInfo', appContext.referalInfo);
      scope.setTag('release', `unknown-charge-app@${appContext.release}`);
      scope.setTag(
        'viewport',
        (isMobile && 'isMobile') ||
          (isDesktop && 'isDesktop') ||
          (isTablet && 'isTablet'),
      );
    });
  }, []);

  const [error, setError] = useState<Error | undefined>(undefined);

  const handleError = (e: Error) => {
    Sentry.captureException(e);
  };

  useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [handleError, error]);

  return (
    <ErrorContext.Provider value={{ error, setError, sentryClient: Sentry }}>
      <ErrorBoundary fallbackRender={ErrorPage} onError={handleError}>
        {error ? <ErrorPage /> : children}
      </ErrorBoundary>
    </ErrorContext.Provider>
  );
};

export const useSentry = () => {
  const context = useContext(ErrorContext);

  if (!context?.sentryClient) {
    throw new Error("can't call useSentry without ErrorContext");
  }

  return { sentryClient: context.sentryClient };
};

export const useError = () => {
  const context = useContext(ErrorContext);

  if (!context?.setError) {
    throw new Error("can't call useError without ErrorContext");
  }

  return { error: context.error, setError: context.setError };
};
